/**
 * Image uploads (docs/02 §3, docs/07 §1): `POST /v1/uploads` returns a
 * presigned `put_url` for one key, one content length and 15 minutes; the
 * browser then PUTs the bytes straight to R2. The API never sees the file.
 */
import { api, newIdempotencyKey } from "./client";
import { UPLOAD_MAX_BYTES, UPLOAD_MIMES, type UploadTicket } from "./types";

export class UploadError extends Error {
  constructor(
    public readonly code: "asset_not_image" | "asset_too_large" | "put_failed",
    message: string,
  ) {
    super(message);
    this.name = "UploadError";
  }
}

/** The checks the API would make anyway, so a wrong file fails before a
 *  round trip; the API remains the authority (it re-checks and sanitises). */
export function checkFile(file: File): void {
  if (!(UPLOAD_MIMES as readonly string[]).includes(file.type)) {
    throw new UploadError("asset_not_image", `Unsupported type ${file.type}`);
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    throw new UploadError("asset_too_large", `${file.size} bytes`);
  }
}

export function createUpload(
  file: File,
  mime: string = file.type,
): Promise<UploadTicket> {
  return api<UploadTicket>("/v1/uploads", {
    method: "POST",
    body: { name: file.name, mime, bytes: file.size },
    idempotencyKey: newIdempotencyKey(),
  });
}

/** The file's bytes, read up front so the PUT carries exactly the content
 *  length the ticket was signed for (and so the body is a plain buffer in
 *  every runtime). Files are capped at 25 MB, so this is cheap. */
export function readBytes(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () =>
      reject(reader.error ?? new Error("Could not read the file"));
    reader.readAsArrayBuffer(file);
  });
}

/** The presigned PUT: cross-origin to the blob store, so no cookie and no
 *  custom header beyond the content type the signature covers. */
export async function putUpload(
  ticket: UploadTicket,
  file: File,
  signal?: AbortSignal,
  mime: string = file.type,
): Promise<void> {
  const response = await fetch(ticket.put_url, {
    method: "PUT",
    headers: { "Content-Type": mime },
    body: await readBytes(file),
    credentials: "omit",
    signal,
  });
  if (!response.ok) {
    throw new UploadError("put_failed", `PUT ${response.status}`);
  }
}

/** Ticket then bytes; resolves to the `asset_id` a job can name. */
export async function uploadImage(
  file: File,
  signal?: AbortSignal,
): Promise<string> {
  checkFile(file);
  const ticket = await createUpload(file);
  await putUpload(ticket, file, signal);
  return ticket.asset_id;
}

/** A spreadsheet an ad may carry (§4.1, §30.3): what the API takes
 *  (`backend/ads/uploads.py::SHEET_MIMES`). The bytes decide on the server. */
export const SHEET_MIMES = [
  "text/csv",
  "application/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const;

const XLSX =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** The type a spreadsheet is sent as: the browser's when it names one we
 *  take, else by its extension (a browser may declare a CSV as nothing);
 *  null for a file that is not a spreadsheet. */
export function sheetMime(file: File): string | null {
  if ((SHEET_MIMES as readonly string[]).includes(file.type)) return file.type;
  const name = file.name.toLowerCase();
  if (name.endsWith(".csv")) return "text/csv";
  if (name.endsWith(".xlsx")) return XLSX;
  return null;
}

/** Ticket then bytes for a spreadsheet; resolves to its upload id. */
export async function uploadSheet(
  file: File,
  signal?: AbortSignal,
): Promise<string> {
  const mime = sheetMime(file);
  if (mime === null) {
    throw new UploadError("asset_not_image", `Unsupported type ${file.type}`);
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    throw new UploadError("asset_too_large", `${file.size} bytes`);
  }
  const ticket = await createUpload(file, mime);
  await putUpload(ticket, file, signal, mime);
  return ticket.asset_id;
}
